import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withDevtools,
  withReset,
} from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { withCallStatus } from '@ngrx-traits/signals';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  ApiError,
  LoginCredentials,
  LoginResponse,
  User,
} from '@seatix/shared/domain';
import { catchError, EMPTY, finalize, pipe, switchMap, tap } from 'rxjs';
import { AUTH_PROVIDER } from '../providers/auth.provider';

interface AuthState {
  user: User | null;
}

const initialState: AuthState = {
  user: null,
};

export const AuthStore2 = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withReset(),
  withProps(() => ({
    _authProvider: inject(AUTH_PROVIDER),
  })),
  withCallStatus({ collection: 'login' }),
  withCallStatus({ collection: 'logout' }),
  withCallStatus({ collection: 'getIdentity' }),
  withComputed((store) => {
    const isLoading = computed(
      () =>
        store.isLoginLoading() ||
        store.isLogoutLoading() ||
        store.isGetIdentityLoading(),
    );
    const isLoaded = computed(
      () => store.isLoginLoaded() || store.isGetIdentityLoading(),
    );
    const hasError = computed(
      () =>
        !!store.loginError() ||
        !!store.logoutError() ||
        !!store.getIdentityError(),
    );
    const userFullName = computed(() => {
      const user = store.user();
      if (!user) return '';
      return `${user.first_name} ${user.last_name}`;
    });
    const check = toSignal(store._authProvider.check());

    const isAuthenticated = () => {
      const user = store.user();
      return !!user;
    };

    return {
      check,
      isAuthenticated,
      isLoading,
      isLoaded,
      hasError,
      userFullName,
    };
  }),

  withMethods((store) => {
    const provider = store._authProvider;

    const login = rxMethod<{
      credentials: LoginCredentials;
      turnstileToken?: string;
    }>(
      pipe(
        tap(() => store.setLoginLoading()),
        switchMap(({ credentials, turnstileToken }) =>
          provider.login(credentials, turnstileToken).pipe(
            tap((response: LoginResponse) => {
              store.setLoginLoaded();
              patchState(store, {
                user: response.data,
              });
              provider.onLoginSuccess(response);
            }),
            catchError((error: ApiError) => {
              store.setLoginError(error.userMessage);
              provider.onLoginFailure(error);
              return EMPTY;
            }),
          ),
        ),
      ),
    );

    const logout = rxMethod<void>(
      pipe(
        tap(() => store.setLogoutLoading()),
        switchMap(() =>
          provider.logout().pipe(
            tap(() => {
              store.setLogoutLoaded();
              provider.onLogoutSuccess();
            }),
            catchError((error: Error) => {
              store.setLogoutError(error.message);
              provider.onError(error);
              return EMPTY;
            }),
            finalize(() => {
              patchState(store, { user: null });
            }),
          ),
        ),
      ),
    );

    const getIdentity = rxMethod<void>(
      pipe(
        tap(() => store.setGetIdentityLoading()),
        switchMap(() =>
          provider.getIdentity().pipe(
            tap((user: User) => {
              patchState(store, { user });
              store.setGetIdentityLoaded();
            }),
            catchError((error: Error) => {
              store.setGetIdentityError(error.message);
              return EMPTY;
            }),
          ),
        ),
      ),
    );

    return {
      login,
      logout,
      getIdentity,
    };
  }),
  withDevtools('AuthStore2'),
);
